import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bell, Loader2, Check, X } from "lucide-react";
import { getUserTopicSubscriptions } from "@/api/notifications/topics";

const TOPIC_LABELS: Record<string, string> = {
  all_users: "All Users",
  android: "Android",
  ios: "iOS",
  new_summary: "New Summary",
  trending_now: "Trending Now",
  daily_quote: "Daily Quote",
};

interface UserTopicSubscriptionsProps {
  userId: number;
}

export default function UserTopicSubscriptions({ userId }: UserTopicSubscriptionsProps) {
  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ["user-topic-subscriptions", userId],
    queryFn: () => getUserTopicSubscriptions(userId),
  });

  const subMap: Record<string, boolean> = {};
  for (const sub of subscriptions || []) {
    subMap[sub.topic] = sub.is_subscribed;
  }

  const subscribedCount = Object.values(subMap).filter(Boolean).length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Topic Subscriptions
          </div>
          {!isLoading && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-normal">
              {subscribedCount} active
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-1.5">
            {Object.entries(TOPIC_LABELS).map(([topic, label]) => {
              const isSubscribed = subMap[topic] ?? false;
              return (
                <div key={topic} className="flex items-center justify-between py-0.5">
                  <span className="text-xs">{label}</span>
                  {isSubscribed ? (
                    <Check className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <X className="h-3.5 w-3.5 text-muted-foreground/40" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
